// Every change to how a project, or global data, is set up: who made it and when, newest
// first. See docs/Y2026-Q4/design-operator-actions-and-config-history.md.
import type { ReactNode } from "react";

import {
  type ConfigChange,
  type HistoryScope,
  useSettingsHistory,
} from "../api/hooks/useSettingsHistory";
import styles from "../routes/ProjectStatusTab.module.css";
import { formatWhen } from "../utils/formatWhen";
import own from "./SettingsHistory.module.css";

const who = (change: ConfigChange): string =>
  change.actor_agent
    ? `the ${change.actor_agent.replace(/_/g, " ")} agent`
    : (change.actor_email ?? "unknown");

export const SettingsHistory = ({
  scope,
  intro,
}: {
  scope: HistoryScope | null;
  intro: ReactNode;
}) => {
  const history = useSettingsHistory(scope);
  const changes = history.data?.pages.flatMap((p) => p.changes) ?? [];
  return (
    <div className={styles.tab}>
      <p className={styles.muted}>{intro}</p>
      {history.isLoading ? (
        <p className={styles.muted}>Loading the history…</p>
      ) : history.error ? (
        <p className={styles.error}>Could not load the settings history.</p>
      ) : changes.length === 0 ? (
        <p className={styles.muted}>No settings changes yet.</p>
      ) : (
        <ul className={own.changes}>
          {changes.map((c) => (
            <li key={c.change_id} className={own.change}>
              <div>{c.summary}</div>
              <div className={own.meta}>
                {who(c)} · {formatWhen(c.at)}
              </div>
            </li>
          ))}
        </ul>
      )}
      {history.hasNextPage && (
        <button
          type="button"
          onClick={() => void history.fetchNextPage()}
          disabled={history.isFetchingNextPage}
        >
          {history.isFetchingNextPage ? "Loading…" : "Show older changes"}
        </button>
      )}
    </div>
  );
};
